import {
  IOption,
  SetupListNavInfoByDepartment,
  ToastInfo,
  LandingPageOptions,
  IAMUserState,
  GeneralPermissions,
  SetupPermission,
} from "../../../../types";
import {
  DEFAULT_TOAST,
  TOAST_MESSAGES,
  IMPLEMENTED_SETUP_KEYS,
  DEPARTMENTS,
  DEFAULT_OPTION,
  SETUP_PERMISSIONS_NONE,
} from "../../constants";
import { getGroupIdOptionBySetupAndStore } from "../group-id";

interface HandleSetupSelectionParams {
  setup: IOption;
  department: IOption;
  store: IOption;
  setupListNavInfoByDepartment: SetupListNavInfoByDepartment;
  setLandingPageToast: (toast: ToastInfo) => void;
  setSelected: React.Dispatch<React.SetStateAction<LandingPageOptions>>;
  iamUserState: IAMUserState;
  permissions: {
    userPermission: GeneralPermissions;
    setupPermission: SetupPermission;
  };
  setPermissions: React.Dispatch<
    React.SetStateAction<{
      userPermission: GeneralPermissions;
      setupPermission: SetupPermission;
    }>
  >;
}

export const handleSetupSelection = ({
  setup,
  department,
  store,
  setupListNavInfoByDepartment,
  setLandingPageToast,
  setSelected,
  iamUserState,
  permissions,
  setPermissions,
}: HandleSetupSelectionParams): boolean => {
  setLandingPageToast(DEFAULT_TOAST);

  if (!isImplementedSetup(setup)) {
    setLandingPageToast(TOAST_MESSAGES.UN_IMPLEMENTED_SETUP(setup.label));
    return false;
  }

  const groupId = getGroupIdOption({
    setup,
    department,
    store,
    iamUserState,
  });

  const departmentId = getDepartmentIdOption({
    setupListNavInfoByDepartment,
    department: department.value,
    storeId: store.value,
  });

  const setupPermission = getSetupItemPermissions({
    setupListNavInfoByDepartment,
    department: department.value,
    setupId: setup.value,
  });

  setPermissions({
    ...permissions,
    setupPermission,
  });

  setSelected((previous) => ({
    ...previous,
    store,
    department,
    setup,
    departmentId,
    groupId,
  }));

  return true;
};

const isImplementedSetup = (setup: IOption): boolean =>
  IMPLEMENTED_SETUP_KEYS.includes(setup.value);

const getGroupIdOption = ({
  setup,
  department,
  store,
  iamUserState,
}: {
  setup: IOption;
  department: IOption;
  store: IOption;
  iamUserState: IAMUserState;
}): IOption => {
  if (department.value !== DEPARTMENTS.ACCOUNTING || !iamUserState.eisGroups) {
    return DEFAULT_OPTION;
  }

  const groupId = getGroupIdOptionBySetupAndStore({
    groupIds: iamUserState.eisGroups.groups,
    setup: setup.value,
    store: store.value,
  });

  return groupId;
};

const getDepartmentIdOption = ({
  setupListNavInfoByDepartment,
  department,
  storeId,
}: {
  setupListNavInfoByDepartment: SetupListNavInfoByDepartment;
  department: string;
  storeId: string;
}): IOption => {
  const departmentIdInfo = setupListNavInfoByDepartment[
    department
  ]?.departmentIds.find((item) => item.storeId === storeId);

  if (!departmentIdInfo) {
    return DEFAULT_OPTION;
  }

  return {
    label: `${departmentIdInfo.departmentId} - ${departmentIdInfo.name}`,
    value: departmentIdInfo.departmentId,
  };
};

const getSetupItemPermissions = ({
  setupListNavInfoByDepartment,
  department,
  setupId,
}: {
  setupListNavInfoByDepartment: SetupListNavInfoByDepartment;
  department: string;
  setupId: string;
}): SetupPermission => {
  const setupItem = setupListNavInfoByDepartment[department]?.setups.find(
    (item) => item.id === setupId,
  );

  return setupItem?.permissions ?? SETUP_PERMISSIONS_NONE;
};
